// src/features/project/SavedViewsBar.tsx
// Saved views: ListSavedViews chips + create / update / delete with a visible-fields
// config. Applying a view is a client-side selection that can switch the workspace tab.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProjectView } from '@react/hooks';
import { Button, Input, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ProjectModal, Field } from './ProjectModal';
import { savedViewTypeMap, idOf, listParams } from './meta';
import type { Entity } from '@core/types/entity';
import type { SavedView } from '@core/types/project';

const VIEW_TYPE_OPTIONS = [
  { value: 'SAVED_VIEW_TYPE_BOARD', labelKey: 'project.view.type.board' },
  { value: 'SAVED_VIEW_TYPE_LIST', labelKey: 'project.view.type.list' },
  { value: 'SAVED_VIEW_TYPE_TIMELINE', labelKey: 'project.view.type.timeline' },
];

interface SavedViewsBarProps {
  projectEntity: Entity;
  activeViewId: string | null;
  onApply: (view: SavedView) => void;
}

export const SavedViewsBar = observer(({ projectEntity, activeViewId, onApply }: SavedViewsBarProps) => {
  const { t } = useI18n();
  const store = useProjectView();
  const projectId = projectEntity.id;

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<SavedView | null>(null);
  const [deleting, setDeleting] = useState<SavedView | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () => store.ListSavedViews(projectId, listParams());
  useEffect(() => {
    reload();
  }, [projectId]);

  const views: SavedView[] = store.items ?? [];

  const del = () =>
    deleting &&
    (async () => {
      setBusy(true);
      try {
        await store.DeleteSavedView(projectId, idOf(deleting));
        setDeleting(null);
        reload();
      } catch {
        /* store surfaces error */
      } finally {
        setBusy(false);
      }
    })();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-muted-foreground">{t('project.views.title')}</span>
      {views.map((v) => {
        const active = idOf(v) === activeViewId;
        return (
          <span key={idOf(v)} className="inline-flex items-center">
            <button
              type="button"
              onClick={() => onApply(v)}
              className={`rounded-l-full border px-3 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {v.name}
              {v.is_shared && <span className="ml-1 opacity-60">· {t('project.views.shared')}</span>}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(v);
                setShowForm(true);
              }}
              aria-label={t('common.edit')}
              className="border-y border-r border-border px-1.5 py-1 text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M11 2l3 3-8 8-4 1 1-4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setDeleting(v)}
              aria-label={t('common.delete')}
              className="rounded-r-full border-y border-r border-border px-1.5 py-1 text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </button>
          </span>
        );
      })}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setEditing(null);
          setShowForm(true);
        }}
      >
        {t('project.views.save')}
      </Button>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {showForm && (
        <SavedViewFormDialog
          projectEntity={projectEntity}
          view={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            reload();
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={t('project.views.deleteTitle')}
        description={t('project.views.deleteDesc')}
        destructive
        isConfirming={busy}
        onConfirm={del}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
});

SavedViewsBar.displayName = 'SavedViewsBar';

function SavedViewFormDialog({
  projectEntity,
  view,
  onClose,
  onSaved,
}: {
  projectEntity: Entity;
  view: SavedView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectView();
  const [name, setName] = useState(view?.name ?? '');
  const [type, setType] = useState(view?.type ?? 'SAVED_VIEW_TYPE_LIST');
  const [isShared, setIsShared] = useState(view?.is_shared ?? false);
  const [visibleFields, setVisibleFields] = useState((view?.visible_fields ?? []).join(', '));
  const [busy, setBusy] = useState(false);

  const parseFields = () =>
    visibleFields
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (view) {
        await store.UpdateSavedView(projectEntity.id, idOf(view), {
          project: projectEntity,
          view: view.entity!,
          saved_view: { ...view, name: name.trim(), type: type as never, is_shared: isShared, visible_fields: parseFields() },
          field_mask: { paths: ['name', 'type', 'is_shared', 'visible_fields'] },
        } as never);
      } else {
        await store.CreateSavedView(projectEntity.id, {
          project: projectEntity,
          name: name.trim(),
          type: type as never,
          is_shared: isShared,
          filters: [],
          sorts: [],
          visible_fields: parseFields(),
        } as never);
      }
      onSaved();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProjectModal
      open
      title={view ? t('project.views.editTitle') : t('project.views.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <Field label={t('project.views.name')} htmlFor="view-name">
          <Input id="view-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('project.views.type')} htmlFor="view-type">
          <Select id="view-type" value={type} onChange={(e) => setType(e.target.value as never)} aria-label={t('project.views.type')}>
            {VIEW_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(savedViewTypeMap[o.value] ?? o.labelKey)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('project.views.visibleFields')} htmlFor="view-fields" hint={t('project.views.visibleFieldsHint')}>
          <Input
            id="view-fields"
            value={visibleFields}
            onChange={(e) => setVisibleFields(e.target.value)}
            placeholder="title, status, assignee, story_points"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          {t('project.views.shareWithTeam')}
        </label>
      </div>
    </ProjectModal>
  );
}
